import React from 'react';
import { Pencil, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { EventSchema } from '../../types/schema';

interface EventDetailsEditorProps {
  selectedEvent: EventSchema | null;
  isEditable: boolean;
  handleEventChange: (eventId: string, field: keyof EventSchema, value: string) => void;
  isEventNameEditing: boolean;
  setIsEventNameEditing: (editing: boolean) => void;
  isEventCodeNameEditing: boolean;
  setIsEventCodeNameEditing: (editing: boolean) => void;
  isEventDescriptionEditing: boolean;
  setIsEventDescriptionEditing: (editing: boolean) => void;
  eventCodeNameError: string | null;
  eventNameInputRef: React.RefObject<HTMLInputElement>;
  eventCodeNameInputRef: React.RefObject<HTMLInputElement>;
  eventDescriptionInputRef: React.RefObject<HTMLTextAreaElement>;
}

const EventDetailsEditor: React.FC<EventDetailsEditorProps> = ({
  selectedEvent,
  isEditable,
  handleEventChange,
  isEventNameEditing,
  setIsEventNameEditing,
  isEventCodeNameEditing,
  setIsEventCodeNameEditing,
  isEventDescriptionEditing,
  setIsEventDescriptionEditing,
  eventCodeNameError,
  eventNameInputRef,
  eventCodeNameInputRef,
  eventDescriptionInputRef,
}) => {
  if (!selectedEvent) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 mb-2">
      <div className="flex items-center justify-between">
        {isEventNameEditing && isEditable ? (
          <input
            ref={eventNameInputRef}
            type="text"
            value={selectedEvent.name}
            onChange={(e) => {
              handleEventChange(selectedEvent.id, 'name', e.target.value);
            }}
            onBlur={() => setIsEventNameEditing(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsEventNameEditing(false);
                e.currentTarget.value = selectedEvent.name; // Revert to original value
              } else if (e.key === 'Enter') {
                e.currentTarget.blur(); // Trigger onBlur to save
              }
            }}
            className="text-gray-900 dark:text-white font-bold text-2xl bg-transparent border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 flex-grow"
            placeholder="Event Friendly Name"
            autoFocus
            disabled={!isEditable}
            tabIndex={1}
          />
        ) : (
          <h2
            className={clsx(
              "flex items-center text-gray-900 dark:text-white font-bold text-2xl px-2 py-1 -mx-2 -my-1 group",
              isEditable ? "cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md" : ""
            )}
            onClick={() => isEditable && setIsEventNameEditing(true)}
          >
            {selectedEvent.name || 'Unnamed Event'}
            {isEditable && (
              <Pencil size={16} className="ml-2 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </h2>
        )}
        <div className="flex items-center gap-2 ml-4">
          {isEventCodeNameEditing && isEditable ? (
            <div className="relative">
              <input
                ref={eventCodeNameInputRef}
                type="text"
                value={selectedEvent.codeName}
                onChange={(e) => handleEventChange(selectedEvent.id, 'codeName', e.target.value)}
                onBlur={() => setIsEventCodeNameEditing(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setIsEventCodeNameEditing(false);
                    e.currentTarget.value = selectedEvent.codeName;
                  } else if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                className={clsx(
                  `px-2 py-1 rounded-md text-sm font-mono bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border`,
                  eventCodeNameError ? 'border-red-500' : 'border-transparent',
                  !isEditable && 'opacity-50 cursor-not-allowed'
                )}
                placeholder="event_code_name"
                autoFocus
                disabled={!isEditable}
                tabIndex={2}
              />
              {eventCodeNameError && (
                <div className="absolute top-full left-0 mt-1 text-xs text-red-500 flex items-center">
                  <AlertCircle size={12} className="inline mr-1" />
                  {eventCodeNameError}
                </div>
              )}
            </div>
          ) : (
            <span
              className={clsx(
                "inline-flex items-center px-2 py-1 rounded-md text-sm font-mono font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 transition-colors group",
                isEditable ? "cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800" : "opacity-50 cursor-not-allowed"
              )}
              onClick={() => isEditable && setIsEventCodeNameEditing(true)}
            >
              {selectedEvent.codeName || 'set_code_name'}
              {isEditable && (
                <span className="flex items-center w-0 overflow-hidden group-hover:w-6 transition-all duration-300">
                  <Pencil size={14} className="ml-1 text-blue-600 group-hover:text-blue-800 dark:group-hover:text-blue-200" />
                </span>
              )}
            </span>
          )}
        </div>
      </div>
      {isEventDescriptionEditing && isEditable ? (
        <textarea
          ref={eventDescriptionInputRef}
          value={selectedEvent.description}
          onChange={(e) => handleEventChange(selectedEvent.id, 'description', e.target.value)}
          onBlur={() => setIsEventDescriptionEditing(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsEventDescriptionEditing(false);
              e.currentTarget.value = selectedEvent.description;
            } else if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
          }}
          className={clsx(
            "text-gray-700 dark:text-gray-300 text-sm bg-transparent border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none",
            !isEditable && 'opacity-50 cursor-not-allowed'
          )}
          placeholder="Event Description"
          rows={2}
          autoFocus
          disabled={!isEditable}
          tabIndex={3}
        />
      ) : (
        <p
          className={clsx(
            "flex items-center text-gray-700 dark:text-gray-300 text-sm px-2 py-1 -mx-2 -my-1 group",
            isEditable ? "cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md" : ""
          )}
          onClick={() => isEditable && setIsEventDescriptionEditing(true)}
        >
          {selectedEvent.description || 'Add a description for this event...'}
          {isEditable && (
            <span className="flex items-center w-0 overflow-hidden group-hover:w-6 transition-all duration-300">
              <Pencil size={14} className="ml-1 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
            </span>
          )}
        </p>
      )}
    </div>
  );
};

export default EventDetailsEditor;
